import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { ContactGroup } from './contacts'
import { TextInput } from '../../components/form/textInput'
import { Button, ButtonShape } from '../../components/simple/button'
import { IconShare } from '../../icons/library'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { StudentGroupRecipient, StudentRecipient, TeacherRecipient } from './compose/composeTypes'
import { BasicUser } from '../../components/userSelector/users'
import { UserSelector } from '../../components/userSelector/userSelector'
import { loadBasicUsersByIdentifiers } from '../../components/userSelector/userSelectorPlugins'
import { Popups } from '../../providers/popups'

export type EditGroupState = {
  group: ContactGroup | undefined
}

export const NEW_GROUP_STATE: EditGroupState = {
  group: undefined,
}

interface EditGroupModalProps {
  editGroupState: EditGroupState
  setEditGroupState: React.Dispatch<React.SetStateAction<EditGroupState | undefined>>
  setPreloadedRecipients: React.Dispatch<React.SetStateAction<(TeacherRecipient | StudentRecipient)[] | undefined>>
  onSaved: () => void
}

export const EditGroupModal: React.FC<EditGroupModalProps> = ({
  editGroupState,
  setEditGroupState,
  setPreloadedRecipients,
  onSaved,
}) => {
  const [saving, setSaving] = React.useState(false)
  const [name, setName] = React.useState(editGroupState.group?.name || '')
  const [students, setStudents] = React.useState<BasicUser[]>(editGroupState.group?.students || [])
  const [teachers, setTeachers] = React.useState<BasicUser[]>(editGroupState.group?.sharedWithTeachers || [])

  const recipientSearchData = React.useMemo(() => {
    return editGroupState.group ? { group: editGroupState.group.id } : { group: 'new' }
  }, [editGroupState.group])

  const canSave = name.length > 0 && students.length >= 2

  const save = () => {
    setSaving(true)
    const req = editGroupState.group
      ? makeRequest<StudentGroupRecipient | 'deleted'>('/frontend_api/editGroup', {
          name,
          students: students.map((student) => student.id),
          teachers: teachers.map((teacher) => teacher.id),
          id: editGroupState.group!.id,
        }).then((response) => {
          if (response === 'deleted')
            setPreloadedRecipients((pr) => pr && pr.filter((r) => !r.group || r.groupID !== editGroupState.group!.id))
          else
            setPreloadedRecipients(
              (pr) => pr && pr.map((r) => (r.group && r.groupID === response.groupID ? response : r))
            )
          onSaved()
          setEditGroupState(undefined)
        })
      : makeRequest<StudentGroupRecipient>('/frontend_api/makeGroup', {
          name,
          studentIDs: students.map((student) => student.id),
          teacherIDs: teachers.map((teacher) => teacher.id),
        }).then((response) => {
          setPreloadedRecipients((pr) => pr && [...pr, response])
          onSaved()
          setEditGroupState(undefined)
        })
    req.catch(showAxiosErrorAsToast).finally(() => setSaving(false))
  }

  return (
    <ControlledModal
      open={true}
      onOpenChange={() => {
        setEditGroupState(undefined)
      }}
      size="sm"
      disabled={saving}
      title={editGroupState.group ? 'Edit Group' : 'New Group'}
      className="gap-3"
    >
      <TextInput value={name} onChange={setName} placeholder="Group Name" />
      <div className="-mt-3 text-sm">Recipients will see this name when you send to this group.</div>
      {(!editGroupState.group || editGroupState.group?.isCustomGroup) && (
        <UserSelector<BasicUser>
          className="flex-grow"
          selectedUsers={students}
          setSelectedUsers={setStudents}
          fetchUsersByIdentifiersForPaste={loadBasicUsersByIdentifiers('student')}
          userDescriptor="students"
          url="/frontend_api/groupStudentRecipients"
          searchData={recipientSearchData}
        />
      )}
      <div className="flex items-center gap-2">
        <Button
          logo
          shape={ButtonShape.iconCircleSmall}
          className="pointer-events-none"
          tooltip="These teachers can also view and edit this group"
        >
          <IconShare />
        </Button>
        <UserSelector<BasicUser>
          className="flex-grow"
          selectedUsers={teachers}
          setSelectedUsers={setTeachers}
          fetchUsersByIdentifiersForPaste={loadBasicUsersByIdentifiers('teacher')}
          userDescriptor="teachers"
          url="/frontend_api/groupTeacherRecipients"
          searchData={recipientSearchData}
        />
      </div>
      <Button logo className="self-start" onClick={save} disabled={!canSave}>
        Save
      </Button>
    </ControlledModal>
  )
}

export const deleteGroup = async (
  groupID: number,
  setPreloadedRecipients: React.Dispatch<React.SetStateAction<(TeacherRecipient | StudentRecipient)[] | undefined>>
) => {
  if (!(await Popups.simpleConfirm('Are you sure you want to delete this group?'))) return false
  try {
    await makeRequest('/frontend_api/deleteGroup', { id: groupID })
    setPreloadedRecipients((pr) => pr && pr.filter((r) => !r.group || r.groupID !== groupID))
    return true
  } catch (e) {
    showAxiosErrorAsToast(e)
    return false
  }
}
